import { useState } from 'react'
import { Link } from 'react-router-dom'
import { getApiUrl, getAuthHeaders } from '../../config/api'
import toast from 'react-hot-toast'
import { EnvelopeIcon, ArrowLeftIcon, CheckCircleIcon } from '@heroicons/react/24/outline'

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSent, setIsSent] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!email.trim()) {
      setError('请输入邮箱地址')
      return
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('请输入有效的邮箱地址')
      return
    }

    setIsLoading(true)

    try {
      const response = await fetch(getApiUrl('/auth/forgot-password'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ email }),
      })

      const data = await response.json()

      if (response.ok) {
        setIsSent(true)
        toast.success('重置密码邮件已发送！')
      } else {
        setError(data.message || '发送失败，请稍后重试')
        toast.error(data.message || '发送失败')
      }
    } catch (error) {
      setError('网络连接失败，请稍后重试')
      toast.error('网络连接失败')
    } finally {
      setIsLoading(false)
    }
  }

  if (isSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 py-12 px-4 sm:px-6 lg:px-8">
        {/* 返回登录按钮 */}
        <Link
          to="/login"
          className="fixed top-6 left-6 z-20 flex items-center space-x-2 bg-white/80 backdrop-blur-lg rounded-xl px-4 py-2 shadow-lg hover:shadow-xl transition-all duration-300 hover:bg-white/90 text-gray-700 hover:text-blue-600"
        >
          <ArrowLeftIcon className="h-5 w-5" />
          <span className="font-medium">返回登录</span>
        </Link>

        <div className="max-w-md mx-auto mt-20">
          <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-8 text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
              <CheckCircleIcon className="h-10 w-10 text-green-600" />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              邮件已发送
            </h2>
            
            <p className="text-gray-600 mb-6">
              我们已向 <span className="font-medium text-gray-900">{email}</span> 发送了一封包含重置密码链接的邮件。
            </p>
            
            <div className="bg-blue-50 rounded-xl p-4 mb-6">
              <p className="text-sm text-blue-700">
                请检查您的收件箱（包括垃圾邮件文件夹），链接将在 1 小时后过期。
              </p>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => {
                  setIsSent(false)
                  setEmail('')
                }}
                className="w-full py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors"
              >
                使用其他邮箱
              </button>
              
              <Link
                to="/login"
                className="block w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white rounded-xl font-medium transition-colors text-center"
              >
                返回登录
              </Link>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 py-12 px-4 sm:px-6 lg:px-8">
      {/* 返回登录按钮 */}
      <Link
        to="/login"
        className="fixed top-6 left-6 z-20 flex items-center space-x-2 bg-white/80 backdrop-blur-lg rounded-xl px-4 py-2 shadow-lg hover:shadow-xl transition-all duration-300 hover:bg-white/90 text-gray-700 hover:text-blue-600"
      >
        <ArrowLeftIcon className="h-5 w-5" />
        <span className="font-medium">返回登录</span>
      </Link>

      {/* 背景装饰 */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
      </div>

      <div className="relative z-10 max-w-md mx-auto mt-20">
        <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-8">
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
              <EnvelopeIcon className="h-8 w-8 text-white" />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              忘记密码？
            </h2>
            <p className="text-gray-600">
              输入您的邮箱地址，我们将发送重置密码链接
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="text-sm font-medium text-gray-700 block mb-1">
                邮箱地址
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  className={`block w-full pl-10 pr-3 py-3 border rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                    error ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-gray-50/50 hover:bg-white'
                  }`}
                  placeholder="请输入您的邮箱"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value)
                    setError('')
                  }}
                />
              </div>
              {error && (
                <p className="mt-2 text-sm text-red-600">{error}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-3 px-4 border border-transparent text-sm font-medium rounded-xl text-white transition-all duration-200 ${
                isLoading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transform hover:scale-105 shadow-lg hover:shadow-xl'
              }`}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  发送中...
                </div>
              ) : (
                '发送重置链接'
              )}
            </button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-sm text-gray-600">
              想起密码了？{' '}
              <Link 
                to="/login" 
                className="font-medium text-blue-600 hover:text-blue-700 transition-colors"
              >
                返回登录
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ForgotPasswordPage

